// HEAL-44a §5.2 — кнопка «Посмотреть бланк».
//
// Стоит на ДВУХ экранах: на карточке анализа и на экране проверки. Это пункт 9
// тикета («показывать исходный файл до и после подтверждения») и то, что делает
// осмысленной правку строк в 44b: без бланка перед глазами человек правит 5 на 4
// по памяти.

import { useRef, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { openUploadFile, preopenPdfTab } from '@/lib/fileViewer';
import { reportClientError } from '@/lib/client-error-report';
import { isNative, platform } from '@/lib/platform';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { ImageViewer } from './ImageViewer';

type Props = {
  uploadId: number;
  fileSize: number;
  /** Тип файла известен заранее — по нему PDF на вебе получает вкладку прямо по
   *  нажатию, иначе её блокирует браузер (HEAL-78). */
  mimeType?: string;
  /** Файл заведомо нечитаем — предупреждаем до открытия, а не показываем системную ошибку. */
  corrupted?: boolean;
  variant?: 'default' | 'outline';
  className?: string;
};

export function ViewBlankButton({
  uploadId,
  fileSize,
  mimeType,
  corrupted = false,
  variant = 'outline',
  className,
}: Props) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  // Защита от двойного тапа — тот же приём, что в sharePng после ревью тимлида.
  const openingRef = useRef(false);

  const handleOpen = async () => {
    if (openingRef.current) return;
    openingRef.current = true;
    setBusy(true);

    // До первого await — иначе браузер сочтёт вкладку поп-апом (HEAL-78).
    const tab = preopenPdfTab(mimeType);
    let opened = false;

    try {
      if (corrupted) {
        toast({
          title: 'Файл повреждён',
          description: 'Мы не смогли его прочитать — возможно, не откроется и просмотрщик.',
        });
      }

      const result = await openUploadFile(uploadId, fileSize, tab);
      opened = result.kind === 'opened';

      if (result.kind === 'unavailable') {
        toast({
          title: 'Файл недоступен',
          description: 'Не удалось найти исходный файл этого анализа.',
          variant: 'destructive',
        });
      } else if (result.kind === 'image') {
        setImageUrl(result.url);
      }
    } catch (e) {
      // Ошибку отправляем на сервер (HEAL-47). Причин у неё минимум четыре —
      // оборвалась загрузка, не записался временный файл, системный
      // просмотрщик отказался открывать, истекла ссылка, — и снаружи они
      // неразличимы. Без этого единственный способ узнать причину — Xcode и
      // телефон в руках, а бланк смотрят на своих устройствах.
      const err = e as { message?: string; code?: string; response?: { status?: number } };
      reportClientError({
        scope: 'view-blank',
        message: [
          `upload=${uploadId}`,
          `size=${fileSize}`,
          `native=${isNative() ? platform() : 'web'}`,
          err?.code ? `code=${err.code}` : null,
          err?.response?.status ? `http=${err.response.status}` : null,
          err?.message ?? String(e),
        ].filter(Boolean).join(' '),
        stack: null,
        componentStack: null,
      });

      toast({
        title: 'Не удалось открыть файл',
        // Про соединение здесь не говорим: оно чаще всего ни при чём, а совет
        // «проверьте соединение» отправляет человека чинить исправное.
        description: 'Мы записали, что именно пошло не так. Попробуйте ещё раз.',
        variant: 'destructive',
      });
    } finally {
      // Пустая вкладка без бланка хуже, чем никакой: на ошибке и на «файл
      // недоступен» её закрываем.
      if (tab && !opened) tab.close();
      openingRef.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <Button type="button" variant={variant} onClick={handleOpen} disabled={busy} className={className}>
        {busy ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <FileText className="mr-2 h-4 w-4" />
        )}
        Посмотреть бланк
      </Button>

      {imageUrl !== null && (
        <ImageViewer
          url={imageUrl}
          onClose={() => setImageUrl(null)}
          onRefresh={async () => {
            try {
              const result = await openUploadFile(uploadId, fileSize);
              return result.kind === 'image' ? result.url : null;
            } catch {
              return null;
            }
          }}
        />
      )}
    </>
  );
}
